import { h, Fragment } from "@stencil/core";
import { getSoftwareModulesByType, SoftwareType } from "../../../api/publicApi/createCase/software";
import { PrefillData } from "../../../api/publicApi/createCase/createCase";
import { FormStore } from "../stores/formStore";
import { ApiStore } from "../stores/apiStore";
import { TranslationKey } from "../../../utils/loadTranslations";
import { classifications } from "../../../api/publicApi/createCase/classifications";

interface SoftwareModuleSelectProps {
    t: (key: string) => string;
    prefillData: PrefillData | null;
    formStore: FormStore;
    apiStore: ApiStore;
}
interface CheckboxProps {
    t: (key: string) => string;
    prefillData: PrefillData | null;
}

export const SoftwareModuleSelect = ({ t, prefillData, formStore, apiStore }: SoftwareModuleSelectProps) => (
    <ux-select
        required
        label={t("field.softwareModule.label")}
        data-validation-required--msg={t("validation.requiredField")}
        name="softwareModule"
    >
        <option value="" selected={!prefillData?.softwareData?.softwareModule} disabled>
            {t("select.placeholder")}
        </option>
        {getSoftwareModulesByType(apiStore.state.softwares, formStore.state.selectedSoftwareType as string).map(
            (softwareModule) => (
                <option value={softwareModule} selected={softwareModule === prefillData?.softwareData?.softwareModule}>
                    {softwareModule}
                </option>
            )
        )}
    </ux-select>
);

export const SoftwareTypeSelect = ({ t, prefillData, formStore, apiStore }: SoftwareModuleSelectProps) => {
    const { softwareTypes } = apiStore.state;
    const prefilledSoftwareType = prefillData?.softwareData?.softwareType || null;
    const isPrefilledTypeExisting = softwareTypes.includes(prefilledSoftwareType as string);
    fixSoftwareModuleVisibility(isPrefilledTypeExisting, formStore, prefilledSoftwareType);
    return (
        <ux-select
            required
            label={t("field.softwareType.label")}
            name="softwareType"
            onUxChange={(event) => {
                formStore.state.selectedSoftwareType = event.detail.value;
            }}
        >
            <option value="" selected={!isPrefilledTypeExisting} disabled>
                {t("select.placeholder")}
            </option>
            {softwareTypes.map((softwareType) => (
                <option value={softwareType} selected={prefillData?.softwareData?.softwareType === softwareType}>
                    {softwareType}
                </option>
            ))}
        </ux-select>
    );
};

// this fixes an issue where the softwareModules where not visible with a correct prefilled softwareType
const fixSoftwareModuleVisibility = (
    isPrefilledTypeExisting: boolean,
    formStore: FormStore,
    prefilledSoftwareType: SoftwareType | null
) => {
    if (isPrefilledTypeExisting && formStore.state.selectedSoftwareType === null) {
        formStore.state.selectedSoftwareType = prefilledSoftwareType as SoftwareType;
        setTimeout(() => {
            formStore.forceUpdate("selectedSoftwareType");
        }, 0);
    }
};

export const SoftwareClassificationSelect = ({ t, prefillData }: CheckboxProps) => {
    const isPrefilledClassificationExisting = classifications.includes(
        prefillData?.softwareData?.softwareProblemClassification as string
    );
    return (
        <ux-select
            required
            label={t("field.softwareProblemClassification.label")}
            data-validation-required--msg={t("validation.requiredField")}
            name="softwareProblemClassification"
        >
            <option value="" selected={!isPrefilledClassificationExisting} disabled>
                {t("select.placeholder")}
            </option>
            {classifications.map((classification) => (
                <option
                    value={classification}
                    selected={classification === prefillData?.softwareData?.softwareProblemClassification}
                >
                    {t(`softwareProblemClassification.${classification}` as TranslationKey)}
                </option>
            ))}
        </ux-select>
    );
};
export const InstallationCheckbox = ({ t, prefillData }: CheckboxProps) => (
    <ux-checkbox
        label={t("field.softwareInstallationProblem.label")}
        name="installationProblem"
        checked={prefillData?.softwareData?.isInstallationProblem}
    ></ux-checkbox>
);

export const LicenseCheckbox = ({ t, prefillData }: CheckboxProps) => (
    <ux-checkbox
        label={t("field.softwareLicenseIssue.label")}
        name="licenseIssue"
        checked={prefillData?.softwareData?.isLicenseIssue}
    ></ux-checkbox>
);

export const SoftwareInputs = ({ t, prefillData, formStore, apiStore }: SoftwareModuleSelectProps) => {
    const { showSoftwareFields, showSoftwareModules } = formStore.state;
    if (!showSoftwareFields) {
        return null;
    }
    return (
        <Fragment>
            <div class="ux-row">
                <div class="ux-col-12 ux-col-md-6">
                    <div class="ux-u-margin-bottom-20">
                        <SoftwareClassificationSelect t={t} prefillData={prefillData} />
                    </div>
                </div>
            </div>
            <div class="ux-row">
                <div class="ux-col-12 ux-col-md-6">
                    <div class="ux-u-margin-bottom-20">
                        <InstallationCheckbox t={t} prefillData={prefillData} />
                    </div>
                </div>
                <div class="ux-col-12 ux-col-md-6">
                    <div class="ux-u-margin-bottom-20">
                        <LicenseCheckbox t={t} prefillData={prefillData} />
                    </div>
                </div>
            </div>
            <div class="ux-row">
                <div class="ux-col-12 ux-col-md-6">
                    <div class="ux-u-margin-bottom-20">
                        <SoftwareTypeSelect t={t} prefillData={prefillData} formStore={formStore} apiStore={apiStore} />
                    </div>
                </div>
                {showSoftwareModules && (
                    <div class="ux-col-12 ux-col-md-6">
                        <div class="ux-u-margin-bottom-20">{/* this is only for styles */}</div>
                    </div>
                )}
            </div>
            <div class="ux-row">
                {showSoftwareModules && (
                    <div class="ux-col-12 ux-col-md-6">
                        <div class="ux-u-margin-bottom-20">
                            <SoftwareModuleSelect t={t} prefillData={prefillData} formStore={formStore} apiStore={apiStore} />
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    );
};
